import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { CreateUserData, UserType, SchoolLevel, Grade } from '../../services/adminService';
import { X, User, Mail, Phone, Lock, GraduationCap, School, ChevronDown, UserPlus, Loader2 } from 'lucide-react';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    whatsapp: '',
    first_name: '',
    last_name: '',
    password: '',
    user_type: 'USER',
    school_level: undefined,
    grade: undefined
  });

  const [availableGrades, setAvailableGrades] = useState<string[]>([]);

  useEffect(() => {
    const primaryGrades = ['0', '1', '2', '3', '4', '5', '6', '7'];
    const secondaryGrades = ['form1', 'form2', 'form3', 'form4', 'form5', 'form6'];
    
    if (formData.school_level === 'primary') {
      setAvailableGrades(primaryGrades);
    } else if (formData.school_level === 'secondary') {
      setAvailableGrades(secondaryGrades);
    } else {
      setAvailableGrades([]);
    }
    setFormData(prev => ({ ...prev, grade: undefined }));
  }, [formData.school_level]);

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.email && !formData.whatsapp) {
      newErrors.contact = "Either Email or WhatsApp number is required";
    }

    if (!formData.first_name) newErrors.first_name = "First name is required";
    if (!formData.last_name) newErrors.last_name = "Last name is required";
    if (!formData.password) newErrors.password = "Password is required";
    
    if (formData.school_level && !formData.grade) {
      newErrors.grade = "Grade is required when school level is selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addUserMutation = useMutation({
    mutationFn: adminService.createUser,
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error) => {
      setErrors({ submit: error.toString() });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const cleanedData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value !== '')
      ) as CreateUserData;
      
      addUserMutation.mutate(cleanedData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-slate-800/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative p-6 border-b border-white/10">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-violet-500 to-purple-500 rounded-full opacity-20 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Add New User</h2>
                <p className="text-xs text-slate-400">Create a new user account</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {errors.contact && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {errors.contact}
            </div>
          )}

          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="email"
              placeholder="Email (Optional if WhatsApp provided)"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50 transition-colors"
            />
          </div>

          {/* WhatsApp */}
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="tel"
              placeholder="WhatsApp Number (Optional if Email provided)"
              value={formData.whatsapp}
              onChange={e => setFormData({...formData, whatsapp: e.target.value})}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50 transition-colors"
            />
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={e => setFormData({...formData, first_name: e.target.value})}
                  className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none transition-colors ${
                    errors.first_name ? 'border-red-500/50' : 'border-white/10 focus:border-violet-500/50'
                  }`}
                />
              </div>
              {errors.first_name && (
                <p className="text-red-400 text-xs mt-1 ml-1">{errors.first_name}</p>
              )}
            </div>
            <div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={e => setFormData({...formData, last_name: e.target.value})}
                  className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none transition-colors ${
                    errors.last_name ? 'border-red-500/50' : 'border-white/10 focus:border-violet-500/50'
                  }`}
                />
              </div>
              {errors.last_name && (
                <p className="text-red-400 text-xs mt-1 ml-1">{errors.last_name}</p>
              )}
            </div>
          </div>

          {/* Password */}
          <div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})}
                className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none transition-colors ${
                  errors.password ? 'border-red-500/50' : 'border-white/10 focus:border-violet-500/50'
                }`}
              />
            </div>
            {errors.password && (
              <p className="text-red-400 text-xs mt-1 ml-1">{errors.password}</p>
            )}
          </div>

          {/* User Type */}
          <div className="relative">
            <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <select
              value={formData.user_type}
              onChange={e => setFormData({...formData, user_type: e.target.value as UserType})}
              className="w-full pl-10 pr-10 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-violet-500/50 transition-colors"
            >
              <option value="USER" className="bg-slate-800">User</option>
              <option value="ADMIN" className="bg-slate-800">Admin</option>
            </select>
          </div>

          {formData.user_type === 'USER' && (
            <>
              {/* School Level */}
              <div className="relative">
                <School className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                <select
                  value={formData.school_level || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    school_level: e.target.value as SchoolLevel || undefined
                  })}
                  className="w-full pl-10 pr-10 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-violet-500/50 transition-colors"
                >
                  <option value="" className="bg-slate-800">Select School Level</option>
                  <option value="primary" className="bg-slate-800">Primary</option>
                  <option value="secondary" className="bg-slate-800">Secondary</option>
                </select>
              </div>

              {/* Grade */}
              {formData.school_level && (
                <div>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <select
                      value={formData.grade || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        grade: e.target.value as Grade || undefined
                      })}
                      className={`w-full pl-10 pr-10 py-3 bg-white/5 border rounded-xl text-sm text-white appearance-none cursor-pointer focus:outline-none transition-colors ${
                        errors.grade ? 'border-red-500/50' : 'border-white/10 focus:border-violet-500/50'
                      }`}
                    >
                      <option value="" className="bg-slate-800">Select Grade</option>
                      {availableGrades.map(grade => (
                        <option key={grade} value={grade} className="bg-slate-800">
                          {grade.charAt(0).toUpperCase() + grade.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.grade && (
                    <p className="text-red-400 text-xs mt-1 ml-1">{errors.grade}</p>
                  )}
                </div>
              )}
            </>
          )}

          {errors.submit && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addUserMutation.isPending}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 transition-opacity text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {addUserMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Add User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
